import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const CondorLogo = () => (
  <svg width="60" height="60" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path 
      d="M50 10 C35 15, 20 25, 15 40 C10 55, 15 70, 25 80 C35 85, 50 85, 65 80 C75 70, 80 55, 85 40 C80 25, 65 15, 50 10 Z M40 35 C38 33, 38 30, 40 28 C42 30, 42 33, 40 35 Z M60 35 C58 33, 58 30, 60 28 C62 30, 62 33, 60 35 Z" 
      fill="currentColor"
    />
    <path 
      d="M15 45 C5 40, 2 45, 8 50 C12 48, 15 45, 15 45 Z M85 45 C95 40, 98 45, 92 50 C88 48, 85 45, 85 45 Z" 
      fill="currentColor"
    />
  </svg>
);

const UnifiedLogin = () => {
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState(null);
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.username, formData.password);

      console.log('Login result:', result);

      if (result.success && result.multipleAccounts) {
        // Show account selector
        setAccounts(result.accounts);
        setLoading(false);
      } else if (!result.success) {
        setError(result.error || 'Invalid credentials. Please try again.');
        setLoading(false);
      }
      // If result.success and no multipleAccounts, the login function handles redirect

    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Invalid credentials. Please try again.');
      setLoading(false);
    }
  };

  const handleAccountSelect = async (organizationId) => {
    setLoading(true);
    setError('');

    try {
      const result = await login(formData.username, formData.password, organizationId);

      if (!result.success) {
        setError(result.error || 'Failed to log in to selected organization.');
        setLoading(false);
      }
      // If successful, the login function handles redirect
    } catch (err) {
      console.error('Account select error:', err);
      setError(err.response?.data?.message || 'Failed to log in to selected organization.');
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // If accounts are available, show account selector
  if (accounts) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div className="text-center">
            <div className="flex justify-center items-center space-x-3 mb-6">
              <div className="text-indigo-600">
                <CondorLogo />
              </div>
              <span className="text-3xl font-bold text-gray-900">Condor</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Select Organization
            </h2>
            <p className="text-gray-600">
              You have access to multiple organizations. Please select one to continue.
            </p>
          </div>

          {/* Account Selector */}
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="divide-y divide-gray-200">
              {accounts.map((account) => (
                <button
                  key={account.organizationId}
                  onClick={() => handleAccountSelect(account.organizationId)}
                  disabled={loading}
                  className="w-full px-6 py-4 text-left hover:bg-indigo-50 focus:outline-none focus:bg-indigo-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {account.organizationName}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {account.role} {account.accessLevel && `• ${account.accessLevel}`}
                      </p>
                    </div>
                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Back Button */}
          <div className="text-center">
            <button
              onClick={() => setAccounts(null)}
              className="text-sm text-indigo-600 hover:text-indigo-500 transition-colors"
            >
              ← Back to login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center items-center space-x-3 mb-6">
            <div className="text-indigo-600">
              <CondorLogo />
            </div>
            <span className="text-3xl font-bold text-gray-900">Condor</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Sign in to your account
          </h2>
          <p className="text-gray-600">
            Enter your credentials to access your dashboard
          </p>
        </div>

        {/* Login Form */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Username */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                value={formData.username}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter your username"
              />
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                value={formData.password}
                onChange={handleInputChange}
                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Enter your password"
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          {/* Additional Links */}
          <div className="mt-6 text-center">
            <a
              href="/forgot-password"
              className="text-sm text-indigo-600 hover:text-indigo-500 transition-colors"
            >
              Forgot your password?
            </a>
          </div>
        </div>

      </div>
    </div>
  );
};

export default UnifiedLogin;